import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import dynamic from 'next/dynamic';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import Input from '@/components/base/Input';
import Label from '@/components/base/Label';
import Loader from '@/components/base/Loader';
import { Trans } from '@/components/tools/translation';
const EmailUpdateVerification = dynamic(
    () => import('@/components/pages/settings/account/EmailUpdateVerification')
);

import useUser from '@/utils/hooks/swr/useUser';

import { deleteApi } from '@/utils/client';

import type { EmailType } from '@/types/brandedTypes/email';
import type { UserType } from '@/types/userType';

import { emailValid } from '@/lib/regex';
import { USER_UNLINK_OAUTH_PROVIDER } from '@/lib/routes/apiEndpoints';

import type { AccountFormType } from '@/components/pages/settings/AccountForm';

export type AccountFormEmailProps = {
    readonly setError: (message: string) => void;
    readonly updateEmail: (email: EmailType) => void;
};

function AccountFormEmail({ setError, updateEmail }: AccountFormEmailProps) {
    const { t } = useTranslation('settings');
    const { user, mutateUser } = useUser();
    const [isLoading, setIsLoading] = useState(false);

    const OAuthProviders = user?.login_providers;
    const isLinkedToProvider = OAuthProviders?.length >= 1;

    async function unlinkProvider(provider: UserType['login_providers'][number]) {
        await deleteApi(USER_UNLINK_OAUTH_PROVIDER[provider.provider], {
            data: { provider_id: provider.provider_id }
        });
        await mutateUser();
    }

    const {
        register,
        formState: { errors }
    } = useFormContext<AccountFormType>();

    const onUnlinkProvider = async () => {
        try {
            setIsLoading(true);
            Promise.all(
                OAuthProviders.map(async (provider) => {
                    await unlinkProvider(provider);
                })
            );
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            setError('errors:error_unlink_provider');
        }
    };

    return (
        <div>
            <Label required htmlFor="email">
                {t('account_page.email')}
            </Label>
            <Input
                id="email"
                type="email"
                name="email"
                placeholder={t('account_page.enter_your_email')}
                error={errors?.email?.message}
                disabled={isLinkedToProvider}
                {...register('email', {
                    required: t('errors:error_email_required'),
                    pattern: {
                        value: emailValid,
                        message: t('errors:error_email_email')
                    }
                })}
            />
            {isLinkedToProvider ? (
                <p className="mt-1 flex items-center font-GMedium text-xs text-icoGray-400 dark:text-icoDarkMode-wolf">
                    <span>
                        <Trans
                            i18nKey="settings:account_page.email_linked_to_provider"
                            components={{
                                unlink: (
                                    <button
                                        type="button"
                                        disabled={isLoading}
                                        onClick={onUnlinkProvider}
                                        className="text-icoBlue underline"
                                    />
                                )
                            }}
                        />
                    </span>
                    {isLoading ? <Loader className="ml-1 h-3 w-3 text-icoBlue" /> : null}
                </p>
            ) : null}
            {user.temp_email ? (
                <EmailUpdateVerification email={user.temp_email} updateEmail={updateEmail} />
            ) : null}
        </div>
    );
}

export default AccountFormEmail;
